import { useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { supabase } from '../../utils/supabase'
import { L } from '../ui/LavDropdown'
import { MbIc } from './mbBits'
import { canEmployeePerformService, isDateClosed } from '../../utils/appointmentUtils'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * rescheduleShared — data + date math for the Reschedule (move) flow, from the
 * Claude Design "appointment-reschedule.jsx" module. A reschedule changes only
 * the date, time and groomer; pets, services, duration and price carry over.
 * The verdict for a slot is open · busy (overlaps a booking) · outside (before
 * open / past close) · closed (shop closed that day). Busy and after-hours
 * times stay pickable and warn instead of block.
 */

/* ── slot grid ── */
export const APR_STEP = 30            // minutes between offered slots
export const APR_EARLIEST = 6 * 60    // "show after-hours" window
export const APR_LATEST = 22 * 60
export const APR_NEXT_HORIZON = 21    // days scanned for "Suggested openings"

/* ── helpers ── */
export const timeToMins = (t) => {
	const [h, m] = (t || '').split(':').map(Number)
	if (Number.isNaN(h)) return null
	return (h || 0) * 60 + (m || 0)
}
export const minsToVal = (m) => String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0')
export const fmtClock = (mins) => {
	const h = Math.floor(mins / 60) % 24, m = mins % 60
	return (((h + 11) % 12) + 1) + ':' + String(m).padStart(2, '0') + ' ' + (h >= 12 ? 'PM' : 'AM')
}
// '8:30 – 10:40 AM' (meridiem once when both sides share it), '11:00 AM – 1:10 PM' otherwise
export const fmtRange = (s, e) => {
	const a = fmtClock(s), b = fmtClock(e)
	return (a.slice(-3) === b.slice(-3) ? a.slice(0, -3) : a) + ' – ' + b
}
export const addDays = (ds, n) => dayjs(ds).add(n, 'day').format('YYYY-MM-DD')
export const fmtFull = (ds) => dayjs(ds).format('ddd, MMM D')       // 'Tue, Jun 9'
export const fmtJump = (ds) => dayjs(ds).format('MMM D')            // 'Jun 9'
export const weekdayName = (ds) => dayjs(ds).format('dddd')
export const firstName = (name) => (name || '').split(' ')[0]
export const uniq = (arr) => arr.filter((x, i) => x && arr.indexOf(x) === i)
export const empFullName = (e) => (e ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : '')
export const todayStr = (tz) => dayjs().tz(tz).format('YYYY-MM-DD')

/* ── shop hours for one day (merchant.weekly_hours: { '0'..'6': [{ start: 'HH:mm', end: 'HH:mm' }] }) ── */
export function dayHours(merchant, ds) {
	const dow = dayjs(ds).day()
	const raw = merchant?.weekly_hours?.[String(dow)] || []
	const intervals = raw
		.map((i) => ({ from: timeToMins(i.start), to: timeToMins(i.end) }))
		.filter((i) => i.from != null && i.to != null && i.to > i.from)
		.sort((a, b) => a.from - b.from)
	if (isDateClosed(ds, merchant?.closed_recurring_dates) || !intervals.length) {
		return { closed: true, intervals: [], open: null, close: null }
	}
	return {
		closed: false,
		intervals,
		open: intervals[0].from,
		close: intervals[intervals.length - 1].to,
	}
}

/* ── groomers ── */
export function groomerNames(empIds, employees, short = false) {
	if (!empIds || !empIds.length) return 'Any groomer'
	return empIds.map((id) => {
		const e = employees.find((x) => x.id === id)
		if (!e) return 'Any groomer'
		return short ? firstName(empFullName(e)) : empFullName(e)
	}).join(' & ')
}
export const groomerEmpIds = (choice, bookedIds) => (choice === 'booked' ? bookedIds : [choice])

// 'As booked' first, then every active groomer able to do all of the visit's
// services (a single booked groomer isn't listed twice).
export function groomerOptions(bookedIds, employees, serviceIds = []) {
	const opts = []
	if (bookedIds.length) {
		opts.push({
			id: 'booked',
			label: groomerNames(bookedIds, employees),
			meta: 'as booked',
			sub: bookedIds.length > 1 ? 'One groomer per pet, as booked' : null,
		})
	}
	employees.forEach((e) => {
		if (bookedIds.length === 1 && bookedIds[0] === e.id) return
		if (!serviceIds.every((sid) => canEmployeePerformService(e, sid))) return
		opts.push({ id: e.id, label: empFullName(e) })
	})
	return opts
}

/* ── bookings colliding with [start, end) for the groomers who must be free ── */
export function collisions(ds, start, end, empIds, busyByDate) {
	const hit = (busyByDate[ds] || []).filter((b) => b.start < end && start < b.end)
	if (empIds.length) return hit.filter((b) => empIds.includes(b.empId))
	return hit
}

export const busyRange = (b) => fmtClock(b.start) + '–' + fmtClock(b.end)

/* ── one slot's verdict ── */
export function checkSlot({ ds, start, mins, empIds, busyByDate, merchant, employees }) {
	const end = start + mins
	const H = dayHours(merchant, ds)
	const shop = firstName(merchant?.name) || 'The shop'
	if (H.closed) {
		return {
			kind: 'closed', cap: 'Closed', conflicts: [],
			note: `${shop} is closed on ${weekdayName(ds)}s. Moving here books outside working hours.`,
		}
	}
	const conflicts = collisions(ds, start, end, empIds, busyByDate)
	if (conflicts.length) {
		const who = uniq(conflicts.map((b) => b.empId)).map((id) => firstName(groomerNames([id], employees)))
		const items = conflicts.slice(0, 2)
			.map((b) => `${b.label} (${firstName(groomerNames([b.empId], employees))}, ${busyRange(b)})`)
			.join(' and ')
			+ (conflicts.length > 2 ? ` and ${conflicts.length - 2} more` : '')
		return {
			kind: 'busy', cap: 'Busy', conflicts,
			note: `Overlaps ${items}. Moving here double-books ${who.join(' and ')}.`,
		}
	}
	if (start < H.open) {
		return {
			kind: 'outside', cap: 'Before open', conflicts: [],
			note: `Starts before opening (${fmtClock(H.open)}). Moving here books outside working hours.`,
		}
	}
	if (end > H.close) {
		return {
			kind: 'outside', cap: 'After close', conflicts: [],
			note: `Runs ${end - H.close} mins past closing (${fmtClock(H.close)}). Moving here books outside working hours.`,
		}
	}
	// Inside opening and closing but across a break between two intervals.
	const inside = H.intervals.some((i) => start >= i.from && end <= i.to)
	if (!inside) {
		return {
			kind: 'outside', cap: 'Break', conflicts: [],
			note: `Falls across a break in ${weekdayName(ds)}'s hours. Moving here books outside working hours.`,
		}
	}
	return { kind: 'open', cap: null, conflicts: [], note: null }
}

export function daySlots({ ds, mins, empIds, busyByDate, merchant, employees, all = false }) {
	const H = dayHours(merchant, ds)
	const from = all || H.closed ? APR_EARLIEST : H.open
	const to = all || H.closed ? APR_LATEST : H.close
	const out = []
	for (let m = from; m < to; m += APR_STEP) {
		const c = checkSlot({ ds, start: m, mins, empIds, busyByDate, merchant, employees })
		if (all || c.kind === 'open') out.push({ mins: m, time: minsToVal(m), ...c })
	}
	return out
}

export const openCount = (args) => daySlots({ ...args, all: false }).length

// the next few openings — the first open slot on each of the next days with room
// (after the current start on its own day)
export function nextOpenings({ fromDate, afterMins = null, mins, empIds, busyByDate, merchant, employees, n = 3, horizon = APR_NEXT_HORIZON }) {
	const out = []
	for (let i = 0; i <= horizon && out.length < n; i++) {
		const ds = addDays(fromDate, i)
		const s = daySlots({ ds, mins, empIds, busyByDate, merchant, employees, all: false })
			.find((x) => i > 0 || afterMins == null || x.mins > afterMins)
		if (s) out.push({ date: ds, ...s })
	}
	return out
}

/**
 * useBusyBookings — what's already on the book around the appointment. Loads
 * every booked / pending appointment of the merchant in the requested date
 * range (merchant-local days), excluding the rows being moved, and groups them
 * by day as { start, end } minutes. The loaded range only grows, so paging the
 * week strip back and forth doesn't refetch.
 */
export function useBusyBookings({ merchantId, tz, excludeIds = [], from, to }) {
	const [rows, setRows] = useState([])
	const [loaded, setLoaded] = useState(null) // { from, to }
	const [loading, setLoading] = useState(false)
	const reqRef = useRef(0)
	const excludeKey = excludeIds.join(',')

	useEffect(() => {
		if (!merchantId || !from || !to) return undefined
		if (loaded && loaded.from <= from && loaded.to >= to) return undefined
		const nf = loaded && loaded.from < from ? loaded.from : from
		const nt = loaded && loaded.to > to ? loaded.to : to
		const req = ++reqRef.current
		let cancelled = false
		const run = async () => {
			setLoading(true)
			try {
				const startIso = dayjs.tz(`${nf} 00:00`, tz).utc().toISOString()
				const endIso = dayjs.tz(`${addDays(nt, 1)} 00:00`, tz).utc().toISOString()
				const { data, error } = await supabase
					.from('appointments')
					.select('id, start_ts, end_ts, employee_id, customer_name, pets(name), service_types(name)')
					.eq('merchant_id', merchantId)
					.in('status', ['booked', 'bookPending'])
					.gte('start_ts', startIso)
					.lt('start_ts', endIso)
				if (error) throw error
				if (cancelled || req !== reqRef.current) return
				setRows(data || [])
				setLoaded({ from: nf, to: nt })
			} catch (e) {
				console.error('Error loading bookings for reschedule:', e)
			} finally {
				if (!cancelled && req === reqRef.current) setLoading(false)
			}
		}
		run()
		return () => { cancelled = true }
	}, [merchantId, tz, from, to, loaded])

	const busyByDate = useMemo(() => {
		const exclude = excludeKey ? excludeKey.split(',') : []
		const map = {}
		rows.forEach((r) => {
			if (exclude.includes(r.id)) return
			const s = dayjs(r.start_ts).tz(tz)
			const e = dayjs(r.end_ts).tz(tz)
			const ds = s.format('YYYY-MM-DD')
			const start = s.hour() * 60 + s.minute()
			const sameDay = e.format('YYYY-MM-DD') === ds
			const end = sameDay ? e.hour() * 60 + e.minute() : 24 * 60
			const label = `${r.pets?.name || r.customer_name || 'Appointment'} · ${r.service_types?.name || 'Visit'}`
			if (!map[ds]) map[ds] = []
			map[ds].push({ id: r.id, date: ds, start, end, label, empId: r.employee_id })
		})
		return map
	}, [rows, tz, excludeKey])

	return { busyByDate, loading, loaded }
}

/* ── client notification settings → what the confirm step can send ── */
// What a reschedule can tell the client. The email always goes out when the
// client has an address on file (the designed "Rescheduled" email). Only the
// text is optional: the confirm step's toggle, offered when the shop has a
// messaging number, the client has a phone number and hasn't turned texts off.
export function notifyPlan({ client, merchant }) {
	// Preference: whether the client wants to hear from the shop on a channel
	// (clients.sms_reminders / email_reminders). Possibility: a contact on
	// file for that channel — and, for texts, the shop's own messaging number.
	const wantsText = !!client && client.sms_reminders !== false
	const wantsEmail = !!client && client.email_reminders !== false
	const shopCanText = Boolean(merchant?.twilio_phone_number)
	const usable = [], missing = []
	if (wantsText) {
		if (!shopCanText) missing.push({ kind: 'text', reason: 'no-twilio' })
		else if (client.phone_number) usable.push({ kind: 'text', to: client.phone_number, icon: 'text' })
		else missing.push({ kind: 'text', reason: 'no-contact' })
	}
	if (wantsEmail) {
		if (client.email) usable.push({ kind: 'email', to: client.email, icon: 'mail' })
		else missing.push({ kind: 'email', reason: 'no-contact' })
	}
	return {
		on: wantsText || wantsEmail,                   // the client wants to be told at all
		wantsText,
		wantsEmail,
		usable,                                        // channels that will actually be used
		missing,                                       // wanted channels that can't be used ({ kind, reason })
		can: usable.length > 0,
		preview: usable.length ? usable[0].kind : null, // text wins when both channels are on
		sent: usable.map((u) => u.kind).join('+') || null, // 'text' | 'email' | 'text+email' | null
	}
}

export const notifiedVerb = (sent) => (
	sent === 'email' ? 'emailed' : sent === 'text+email' ? 'texted & emailed' : sent ? 'texted' : null
)
export const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// Pet names read as a list: "Toby", "Toby & Jimmy", "Toby, Jimmy & Bobby".
export function joinNames(names) {
	const list = (names || []).filter(Boolean)
	if (list.length <= 1) return list[0] || ''
	return `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`
}

// The move notice (same wording for text and email). The greeting is its own
// sentence so the client's name never runs into the pet list.
export function buildMoveMessage({ clientName, petNames, merchantName, date, time }) {
	return `Hi ${firstName(clientName)}! ${petNames}’s appointment at ${merchantName} is now ${fmtFull(date)} at ${fmtClock(timeToMins(time))}. See you then!`
}

/* ── after a move: where it came from, and whether the client was told ── */
// moved = { fromStartTs, notified: 'text' | 'email' | 'text+email' | null }
export function MovedBanner({ moved, clientName, tz, dense = false }) {
	if (!moved) return null
	const from = dayjs(moved.fromStartTs).tz(tz)
	const v = notifiedVerb(moved.notified)
	const first = firstName(clientName)
	const fromLabel = `${from.format('ddd, MMM D')} · ${from.format('h:mm A')}`

	if (dense) {
		// design .ap-checkbar.apr-bar
		return (
			<Box sx={{
				display: 'flex', alignItems: 'center', gap: '8px', borderRadius: L.rSm,
				p: '10px 12px', fontSize: '12.5px', fontWeight: 650,
				border: '1.5px solid #DCD5F5', bgcolor: L.primarySoft, color: L.primaryDeep,
			}}>
				<Box sx={{ display: 'flex', flex: 'none', color: L.primary }}><MbIc name="move" size={15} /></Box>
				<Box component="span" sx={{ flex: 1, minWidth: 0 }}>
					Moved from <Box component="strong" sx={{ fontWeight: 750 }}>{fromLabel}</Box>
				</Box>
				<Box component="span" sx={{ flex: 'none', fontSize: '11.5px', fontWeight: 650, opacity: 0.8, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
					{v ? `${capitalize(v)} ${first} · just now` : 'Not notified'}
				</Box>
			</Box>
		)
	}
	// design .mbapd-banner.apr-banner
	return (
		<Box sx={{
			display: 'flex', alignItems: 'center', gap: '11px', borderRadius: '15px', p: '13px 14px',
			border: '1.5px solid #DCD5F5', bgcolor: L.primarySoft, color: L.primaryDeep,
		}}>
			<Box sx={{ flex: 'none', display: 'flex', color: L.primary }}><MbIc name="move" size={17} /></Box>
			<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
				<Box component="strong" sx={{ fontSize: '14.5px', fontWeight: 750 }}>Moved from {fromLabel}</Box>
				<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, opacity: 0.85 }}>
					{v ? `${capitalize(v)} ${first} the new time · just now` : `${first} was not notified`}
				</Box>
			</Box>
		</Box>
	)
}
